import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import useAxiosPrivate from "hooks/useAxiosPrivate";
import useCurrentUser from "hooks/useCurrentUser";
import {
  PageContainer, DataTable, PremiumCard, Button,
  Field, SelectField, StatusBadge, EmptyState,
} from "components/ui";

const Movements = () => {
  const axiosPrivate = useAxiosPrivate();
  const { user } = useCurrentUser();
  const branch = user?.branch;
  const [searchParams] = useSearchParams();

  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  const [reason, setReason] = useState("");
  const [productId, setProductId] = useState(searchParams.get("productId") || "");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // ── Product name search (mirrors POS product search) ──
  const [productQuery, setProductQuery] = useState("");
  const [productResults, setProductResults] = useState([]);
  const [productSearchLoading, setProductSearchLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchRef = useRef(null);

  useEffect(() => {
    clearTimeout(searchRef.current);
    if (!productQuery.trim()) { setProductResults([]); return; }
    setProductSearchLoading(true);
    searchRef.current = setTimeout(async () => {
      try {
        const res = await axiosPrivate.get(`/api/products/search?q=${encodeURIComponent(productQuery)}`);
        setProductResults(res.data);
      } catch { toast.error("Failed to fetch products"); }
      finally { setProductSearchLoading(false); }
    }, 300);
    return () => clearTimeout(searchRef.current);
  }, [productQuery]);

  const pickProduct = (product) => {
    setProductId(product.id);
    setProductQuery(product.name);
    setShowSuggestions(false);
  };

  const clearProduct = () => {
    setProductId("");
    setProductQuery("");
    setProductResults([]);
  };

  const size = 8;

  // Accepts optional overrides so callers that just changed state (which hasn't
  // re-rendered yet) can pass the intended values instead of relying on stale closures.
  const fetchMovements = async (overrides = {}) => {
    if (!branch) return;
    setLoading(true);
    try {
      const effectivePage = overrides.page ?? page;
      const effectiveReason = overrides.reason ?? reason;
      const effectiveProductId = overrides.productId ?? productId;
      const effectiveStartDate = overrides.startDate ?? startDate;
      const effectiveEndDate = overrides.endDate ?? endDate;

      const params = new URLSearchParams();
      params.append("page", effectivePage);
      params.append("size", size);
      if (effectiveReason) params.append("reason", effectiveReason);
      if (effectiveProductId) params.append("productId", effectiveProductId);
      if (effectiveStartDate) params.append("startDate", effectiveStartDate);
      if (effectiveEndDate) params.append("endDate", effectiveEndDate);
      const res = await axiosPrivate.get(`/api/branches/${branch.id}/movements?${params.toString()}`);
      setMovements(res.data.content);
      setTotalPages(res.data.page.totalPages);
      setTotalElements(res.data.page.totalElements);
    } catch (_) {
      toast.error("Failed to load movements");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMovements(); }, [branch, page]);

  const apply = (e) => {
    e.preventDefault();
    setPage(0);
    fetchMovements({ page: 0 });
  };

  const clear = () => {
    setReason(""); clearProduct(); setStartDate(""); setEndDate(""); setPage(0);
    fetchMovements({ page: 0, reason: "", productId: "", startDate: "", endDate: "" });
  };

  if (!branch) {
    return (
      <PageContainer>
        <PremiumCard>
          <EmptyState title="No branch assigned" />
        </PremiumCard>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PremiumCard
        className="mb-3"
        title="Filters"
        subtitle="Narrow the movement history"
      >
        <form onSubmit={apply} style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", alignItems: "end" }}>
          <Field label="From" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          <Field label="To"   type="date" value={endDate}   onChange={(e) => setEndDate(e.target.value)} />
          <SelectField label="Type" value={reason} onChange={(e) => setReason(e.target.value)}>
            <option value="">All</option>
            <option value="PURCHASE">Purchase</option>
            <option value="SALE">Sale</option>
            <option value="ADJUSTMENT">Adjustment</option>
            <option value="TRANSFER">Transfer</option>
            <option value="RETURN">Return</option>
          </SelectField>

          <div style={{ position: "relative" }}>
            <Field
              label="Product"
              placeholder="Search product name…"
              value={productQuery}
              onChange={(e) => {
                setProductQuery(e.target.value);
                setShowSuggestions(true);
                if (productId) setProductId("");
              }}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
            />
            {showSuggestions && productQuery.trim() && (
              <div style={{
                position: "absolute", top: "100%", left: 0, right: 0, zIndex: 20,
                background: "#12141f", border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: 8, marginTop: 4, maxHeight: 220, overflowY: "auto",
                boxShadow: "0 12px 32px rgba(0,0,0,0.4)",
              }}>
                {productSearchLoading ? (
                  <div style={{ padding: "10px 12px", fontSize: 12, color: "rgba(255,255,255,0.4)" }}>
                    Searching…
                  </div>
                ) : productResults.length > 0 ? (
                  productResults.map((p) => (
                    <div
                      key={p.id}
                      onMouseDown={() => pickProduct(p)}
                      style={{ padding: "8px 12px", cursor: "pointer", fontSize: 13 }}
                    >
                      {p.name}
                    </div>
                  ))
                ) : (
                  <div style={{ padding: "10px 12px", fontSize: 12, color: "rgba(255,255,255,0.4)" }}>
                    No products found
                  </div>
                )}
              </div>
            )}
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            <Button type="submit" variant="primary">Apply</Button>
            <Button type="button" variant="secondary" onClick={clear}>Clear</Button>
          </div>
        </form>
      </PremiumCard>

      <DataTable
        title="Stock movements"
        subtitle={`${totalElements} record${totalElements !== 1 ? "s" : ""}`}
        loading={loading}
        data={movements}
        rowKey={(m) => m.id}
        search={false}
        paginate={false}
        emptyTitle="No movements found"
        columns={[
          { key: "createdAt", header: "Date", render: (m) => <span className="t-muted t-mono" style={{ fontSize: 12 }}>{new Date(m.createdAt).toLocaleString()}</span> },
          { key: "product",   header: "Product", render: (m) => m.product?.name || "—" },
          {
            key: "quantityChange", header: "Change", align: "right",
            render: (m) => (
              <span className={m.quantityChange > 0 ? "t-success" : "t-danger"} style={{ fontWeight: 600, fontFamily: "var(--font-mono)" }}>
                {m.quantityChange > 0 ? "+" : ""}{m.quantityChange}
              </span>
            ),
          },
          { key: "reason",    header: "Type",      render: (m) => <StatusBadge value={m.reason} /> },
          { key: "reference", header: "Reference", render: (m) => <span className="t-mono t-muted">{m.reference || "—"}</span> },
        ]}
        footer={
          totalPages > 1 ? (
            <div className="dt-foot">
              <span>Page <strong>{page + 1}</strong> of {totalPages} · {totalElements} record{totalElements !== 1 ? "s" : ""}</span>
              <div style={{ display: "inline-flex", gap: 6 }}>
                <Button variant="ghost" size="sm" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>‹ Prev</Button>
                <Button variant="ghost" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}>Next ›</Button>
              </div>
            </div>
          ) : null
        }
      />
    </PageContainer>
  );
};

export default Movements;